import { Injectable } from '@angular/core';
import * as Highcharts from 'highcharts';
import moment from 'moment';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';
import {
  RemoteDiagnosticsChartValue,
  RemoteDiagnosticsParameter,
  RemoteDiagnosticsStore,
  UnitsStore,
  VddDetailsListStore,
} from '@store';
import { map, switchMap } from 'rxjs/operators';
import { SUFFIX, convertCelsiusToFahrenheit } from '../../../../shared/common/units';
import { DiagnosticsHelpers } from './diagnostics-helpers';

@Injectable({ providedIn: 'root' })
export class DiagnosticsChartService {
  public updateFlag: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public currentSeriesLength: BehaviorSubject<number> = new BehaviorSubject(0);
  public currentSeriesNames: BehaviorSubject<string[]> = new BehaviorSubject([]);

  private colors = [
    '#1f77b4',
    '#ff7f0e',
    '#2ca02c',
    '#d62728',
    '#9467bd',
    '#8c564b',
    '#e377c2',
    '#7f7f7f',
    '#bcbd22',
    '#17becf',
    '#aec7e8',
    '#ffbb78',
    '#98df8a',
    '#ff9896',
    '#c5b0d5',
    '#c49c94',
    '#f7b6d2',
    '#c7c7c7',
    '#dbdb8d',
    '#9edae5',
    '#393b79',
    '#637939',
    '#8c6d31',
    '#843c39',
    '#7b4173',
    '#5254a3',
    '#8ca252',
    '#b5cf6b',
    '#cedb9c',
    '#3182bd',
  ];

  private markerSymbols = ['circle', 'square', 'diamond', 'triangle', 'triangle-down'];
  private _chartOptions: Highcharts.Options = {
    chart: {
      zooming: {
        type: 'xy',
      },
      animation: {
        duration: 500,
        easing: 'easeOutBounce',
      },
      height: 209,
    },
    tooltip: {
      className: 'trending-chart-tooltip',
      followPointer: true,
      shared: true,
      shape: 'rect',
      backgroundColor: '#fff',
      borderColor: '#fff',
      borderRadius: 8,
      borderWidth: 0,
      useHTML: true,
      outside: true,

      formatter: function () {
        const wrapperStyles = `
          display: flex;
          padding: 8px 16px;
          flex-direction: column;
          gap: 1px;
        `;

        const separatorStyle = `
          display: flex;
          width: 100%;
          background-color: #e7e7e7;
          height: 1px;
          margin: 6px 0;
        `;

        const entryStyles = `
          width: 100%;
          display: flex;
          justify-content: start;
          align-items:center;
        `;

        const unitStyles = `
          padding-left: 16px;
          font-weight: bold;
        `;

        // eslint-disable-next-line
        const content = this.points.reduce(function (acc, point: any, index) {
          const nameLine =
            point.series.name.split('-').length > 1
              ? `<b>${point.series.name.split('-')[0]}</b>
            <br/>
            <small>${point.series.name.split('-')[1]}</small>`
              : `<b>${point.series.name}</b>`;

          const valueLine = `<div style="${entryStyles}">
          <span style="color: ${point.series.color}; margin-right: 5px">\u25CF</span>

          <span>
            ${nameLine}
          </span>

            <span style="${unitStyles}">${Math.round(point.y * 100) / 100}${
            point.series['tooltipOptions'].valueSuffix
          }</span></div>`;

          if (index === 0) {
            const dateText = moment(new Date(point.key)).utcOffset(0).format('DD.M.YYYY');

            const dateLine = `<div style="${entryStyles}"><span>${dateText}</span></div>`;
            const separator = `<div style="${separatorStyle}"></div>`;

            return `${dateLine}${separator}${valueLine}`;
          }

          return `${acc}${valueLine}`;
        }, '<b>' + this.x + '</b>');

        const wrapper = `<span style="${wrapperStyles};">${content}</span>`;

        return wrapper;
      } as any,
    },
    credits: {
      enabled: false,
    },
    rangeSelector: {
      enabled: false,
    },
    legend: {
      enabled: true,
      labelFormatter: function () {
        if (this.name.split('-').length > 1)
          return `<div style="margin-bottom:1rem"><b>${this.name.split('-')[0]}</b><br/><span>${
            this.name.split('-')[1]
          }</span></div>`;

        return `<b>${this.name}</b>`;
      },
    },
    title: {
      text: null,
    },
    series: [],
    xAxis: {
      title: null,
      type: 'datetime',
      ordinal: true,
      labels: {
        style: {
          fontSize: '9px',
          fontFamily: 'Arial,sans-serif',
        },
      },
      dateTimeLabelFormats: {
        day: '%H:%M:%S',
      },
      gridLineWidth: 0,
      lineWidth: 0,
      minorGridLineWidth: 0,
      lineColor: 'transparent',
      minorTickLength: 0,
      tickLength: 0,
    },
    yAxis: {
      title: null,
    },
  };

  private isCelsius: boolean;

  get chartOptions(): Highcharts.Options {
    return this._chartOptions;
  }

  numberOfSelectedSensorParameters(gatewaySerial: string): Observable<number> {
    const selectedParameters = this.remoteDiagnosticsStore.getParametersSelectedOnly(gatewaySerial);

    return selectedParameters.pipe(
      switchMap((parameters) => {
        const selectedSensors: Observable<boolean>[] = parameters.map((param) =>
          this.vddDetailsListStore
            .getVddDetail(param.vddDetail.name, param.controlUnit.value)
            .pipe(map((vdd) => DiagnosticsHelpers.isVddSensorFromStructure(vdd?.structure)))
        );

        return selectedSensors.length ? combineLatest(selectedSensors) : of([false]);
      }),
      map((selectedSensors) => selectedSensors.filter((el) => el).length)
    );
  }

  constructor(
    private remoteDiagnosticsStore: RemoteDiagnosticsStore,
    private vddDetailsListStore: VddDetailsListStore,
    private unitsStore: UnitsStore,
    private translateService: TranslateService
  ) {
    this.unitsStore.isTemperatureMetric$.subscribe((isCelsius) => {
      this.isCelsius = isCelsius;
    });
  }

  public clearAll(): void {
    let anythingChanged = false;
    this._chartOptions.series.forEach((series: Highcharts.SeriesLineOptions) => {
      series.data = [];
      anythingChanged = true;
    });
    if (anythingChanged) {
      this.updateFlag.next(true);
      this.emitLength();
      this.emitSeriesNames();
    }
  }

  public deleteSeries(): void {
    if (this._chartOptions.series?.length) {
      this._chartOptions.series = [];
      this.updateFlag.next(true);
      this.emitLength();
      this.emitSeriesNames();
    }
  }

  public clearSeries(parameters: { vddName: string; llName: string }[]): void {
    let anythingChanged = false;
    this.chartOptions.series
      .filter((series) => {
        const nameHasLLName = series.name.split('-').length > 1;
        if (nameHasLLName)
          return parameters
            .map((parameter) => this.getNameChart(parameter.vddName, parameter.llName))
            .some((parameter) => parameter === series.name);

        return parameters
          .map((parameter) => parameter.vddName)
          .some((parameter) => parameter === series.name);
      })
      .forEach((series: Highcharts.SeriesLineOptions) => {
        series.data = [];
        anythingChanged = true;
      });
    if (anythingChanged) {
      this.updateFlag.next(true);
      this.emitLength();
      this.emitSeriesNames();
    }
  }

  public removeSeries(parameters: { vddName: string; llName: string }[]): void {
    const newSeries = this.chartOptions.series.filter((series) => {
      const nameHasLLName = series.name.split('-').length > 1;
      if (nameHasLLName)
        return !parameters
          .map((parameter) => this.getNameChart(parameter.vddName, parameter.llName))
          .some((parameter) => parameter === series.name);

      return !parameters
        .map((parameter) => parameter.vddName)
        .some((parameter) => parameter === series.name);
    });
    if (this._chartOptions.series.length !== newSeries.length) {
      this._chartOptions.series = newSeries;
      this.updateFlag.next(true);
      this.emitLength();
      this.emitSeriesNames();
    }
  }

  public removeAllSeries(): void {
    this._chartOptions.series = [];
    this.updateFlag.next(true);
    this.emitLength();
    this.emitSeriesNames();
  }

  /**
   * @param requestId - requestId of vdd
   * @param vddName - name of vdd
   * @param llName - logical links name
   * @param isDuplicate - check for duplicate names of vddDetail
   */
  public addSeries(requestId: string, vddName: string, llName: string, isDuplicate: boolean): void {
    const name = isDuplicate ? this.getNameChart(vddName, llName) : vddName;
    const seriesExist = this._chartOptions.series.filter(
      (series) => name === series.name
    ) as Highcharts.SeriesLineOptions[];
    const newSeries: Highcharts.SeriesOptionsType = {
      id: requestId,
      name: name,
      type: 'spline',
      data: [],
      marker: {
        enabled: false,
        symbol: seriesExist.length
          ? seriesExist[0].marker.symbol
          : this.markerSymbols[Math.floor(Math.random() * this.markerSymbols.length)],
      },
      color: this.setColorForSerie(seriesExist),
      linkedTo: seriesExist.length ? seriesExist[0].id : undefined, // we link to the first parent. could be chained
    };

    this._chartOptions.series.push(newSeries);
    this.updateFlag.next(true);
    this.emitLength();
    this.emitSeriesNames();
  }

  setColorForSerie(seriesExist: Highcharts.SeriesLineOptions[]): string {
    if (seriesExist.length) {
      return seriesExist[0].color as string;
    }

    //uniq list series by name
    const seriesUniq = [
      ...new Map(this._chartOptions.series.map((series) => [series.name, series])).values(),
    ];

    if (seriesUniq.length < this.colors.length) {
      const seriesColorUniq = seriesUniq.map(
        (serie) => (serie as Highcharts.PlotLineOptions).color
      );
      const colorUnused: string = this.colors.find((color) => !seriesColorUniq.includes(color));

      return colorUnused;
    } else {
      const times = Math.floor(seriesUniq.length / this.colors.length);

      const colorUnused: string = this.colors.find(
        (color) =>
          !seriesUniq
            .slice(this.colors.length * times, this.colors.length * (times + 1))
            .map((serie) => (serie as Highcharts.PlotLineOptions).color)
            .includes(color)
      );

      return colorUnused;
    }
  }

  public addData(chartValues: RemoteDiagnosticsChartValue[]): void {
    chartValues.forEach((chartValue) => {
      const seriesToBeModified = this._chartOptions.series.find(
        (series: Highcharts.SeriesLineOptions) => series.id === chartValue.requestId
      );
      if (seriesToBeModified?.type === 'spline') {
        if (!this.isCelsius && chartValue.unit === SUFFIX.celsius) {
          chartValue.unit = SUFFIX.fahrenheit;
          chartValue.value = Number(
            convertCelsiusToFahrenheit(Number(chartValue.value)).toFixed(1)
          );
        }
        seriesToBeModified.data = [
          ...seriesToBeModified.data,
          [chartValue.wsTime, chartValue.value],
        ];
        seriesToBeModified.tooltip = {
          valueSuffix: chartValue.unit,
        };
      }
    });
    this.updateFlag.next(true);
  }

  private emitLength(): void {
    this.currentSeriesLength.next(this._chartOptions.series.length);
  }

  private emitSeriesNames(): void {
    this.currentSeriesNames.next([
      ...new Set(this._chartOptions.series.map((serie) => serie.name)),
    ]);
  }

  private getNameChart(vddName: string, llName: string): string {
    return vddName + ' - ' + llName;
  }

  public getLLName(parameter: RemoteDiagnosticsParameter): string {
    return (
      this.translateService.instant(parameter.controlUnit.objectProperty) +
      this.translateService.instant(parameter.controlUnit.busType)
    );
  }
}
